// @flow

import type { UseFormReturn } from 'react-hook-form';

import { useCountry } from '@gandi/react-places';
import { selectPersonalId } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { sirenCountries } from 'src/hooks/useMissingOrgInfo';

import type { Contact, FormPage } from '../types';

/** useFormPage return definition of this form */
export const useFormPage = ({
  bu,
  form,
  isEditingMode,
  canCreate,
  contact,
  isCustomer = false,
  studio,
}: {
  bu?: number,
  form: UseFormReturn<any>,
  isEditingMode: boolean,
  isCustomer?: boolean,
  contact?: Contact,
  canCreate: boolean,
  studio?: boolean,
}): FormPage => {
  const { __ } = useTranslator();
  const personalId = useAppSelector(selectPersonalId);

  const countryFieldValue = form.watch('country');
  const COUNTRIES_WITH_ZIP_AFTER_CITY = ['AU', 'CA', 'IE', 'GB', 'NZ', 'US'];
  const isZipAfterCity =
    COUNTRIES_WITH_ZIP_AFTER_CITY.includes(countryFieldValue);

  const {
    isMandatorySubdivision,
    isMandatoryZipCode,
    subdivisions,
    isFetchingCountry,
  } = useCountry(countryFieldValue, bu);

  const typeFieldValue = form.watch('type');

  const isSirenCountry = sirenCountries.includes(form.watch('country'));

  const zipField = {
    label: __('Post code'),
    name: 'zip',
    placeholder: __('Post code'),
    required: isMandatoryZipCode,
  };

  const cityField = {
    label: __('City'),
    name: 'city',
    placeholder: __('Enter the city'),
  };

  const stateField = {
    label: __('State'),
    name: 'state',
    placeholder: __('The state or province'),
    required: isMandatorySubdivision,
    toRender:
      (isFetchingCountry || !!subdivisions.length) &&
      (isMandatorySubdivision || isEditingMode),
  };

  const isFillingIndividualOrga = canCreate
    ? typeFieldValue === 'individual'
    : contact?.type === 'individual';

  if (studio)
    return [
      {
        fieldSet: [
          [
            {
              label: __('Phone number'),
              name: 'phone',
              placeholder: __('01 23 45 67 89'),
            },
          ],
        ],
      },
    ];

  return [
    {
      title: __('Contact details'),
      fieldSet: [
        [
          {
            name: 'type',
            toRender: canCreate,
          },
        ],
        [
          {
            label: __('Organization display name'),
            name: 'name',
            placeholder: __('Enter the organization display name'),
            toRender: !isFillingIndividualOrga && !isCustomer,
            help: __(
              "This is the name that will be displayed on Gandi's website" +
                ' to referer to your organization.',
            ),
          },
        ],
        [
          {
            label: __('Company name'),
            name: 'orgname',
            placeholder: __('Enter the company name'),
            disabled: !!contact?.orgname,
            toRender: !isFillingIndividualOrga,
            help: isCustomer
              ? __(
                  'This is the full real name of the company, association, or public body of your customer.' +
                    'It will be used on invoices and in every legal aspect of your experience at Gandi.',
                )
              : __(
                  'This is the full real name of your organization. It will' +
                    ' be used on invoices and in every legal aspect of your experience at Gandi.',
                ),
          },
        ],
        [
          {
            label: __('First name'),
            name: 'firstname',
            placeholder: __('Enter the first name'),
            disabled:
              isFillingIndividualOrga &&
              !!contact?.firstname &&
              contact.type === 'individual',
          },
          {
            label: __('Last name'),
            name: 'lastname',
            placeholder: __('Enter the last name'),
            disabled:
              isFillingIndividualOrga &&
              !!contact?.lastname &&
              contact.type === 'individual',
          },
        ],
      ],
    },
    {
      title: __('Address'),
      fieldSet: [
        [
          {
            label: __('Country'),
            name: 'country',
            placeholder: __('Select the country'),
          },
        ],
        [
          {
            label: __('Street address'),
            name: 'streetaddr',
            placeholder: __('Enter the street address'),
          },
        ],
        [
          {
            label: __('Apartment, number, suite, building (optional)'),
            name: 'streetaddr2',
            required: false,
            placeholder: __('Enter the additional address details.'),
          },
        ],
        isZipAfterCity ? [cityField] : [zipField, cityField],
        isZipAfterCity ? [stateField, zipField] : [stateField],
      ],
    },
    {
      title: __('Contacts'),
      fieldSet: [
        [
          {
            label: __('Email'),
            name: 'email',
            placeholder: __('Enter the email address'),
            help:
              !isCustomer && isEditingMode
                ? contact?.id === personalId // buying for myself case
                  ? __(
                      'This is the email we will use to contact you in most cases. ' +
                        'You may also want to review your security email in your account settings.',
                    )
                  : __(
                      'This is the email we will use in most cases to contact this organization. ' +
                        'You may also want to review your own security email in your account settings.',
                    )
                : undefined,
          },
        ],
        [
          {
            label: __('Phone number'),
            name: 'phone',
            placeholder: __('01 23 45 67 89'),
          },
        ],
        [
          {
            label: __('Fax'),
            name: 'fax',
            placeholder: __('01 23 45 67 89'),
            required: false,
            toRender: !isFillingIndividualOrga,
          },
        ],
      ],
    },
    {
      title: __('Currency'),
      toRender: !isCustomer,
      fieldSet: [
        [
          {
            label: __('Currency'),
            name: 'currency',
            placeholder: __('Select the currency'),
          },
        ],
      ],
    },
    {
      title: __('Company details'),
      toRender: !isFillingIndividualOrga && !isCustomer,
      fieldSet: [
        [
          {
            label: isSirenCountry
              ? __('SIREN number')
              : __('Company registration ID'),
            name: 'siren',
            placeholder: isSirenCountry
              ? __('Enter the SIREN number')
              : __('Enter the company registration ID'),
            required: false,
          },
        ],
        isSirenCountry
          ? [
              {
                label: __('Routing address'),
                name: 'routing_identifier',
                placeholder: __('Enter the routing address'),
                required: false,
              },
            ]
          : [],
        [
          {
            label: __('VAT Number'),
            name: 'vat_number',
            placeholder: __('Enter the VAT number'),
            required: false,
            help: contact?.vat_number
              ? __(
                  'If you have to change the VAT number, please contact our customer ' +
                    'service.',
                )
              : __(
                  'IMPORTANT: Enter the VAT number with your country code, and without ' +
                    'any spaces. For example: XX999999999',
                ),
          },
        ],
      ],
    },
  ];
};
